/* The sites, from the register rather than from the source.
 *
 * DEPOTS in services/depots.ts used to be the list, which made opening a fourth
 * site an edit to a union type, a constant, two mapping tables, a CSV importer
 * and a provisioning script — six places and a deploy, to record that Hird had
 * rented a building.
 *
 * Now the Locations register is the list and this derives the shape the rest of
 * the app wants from it. Adding a site is adding a row, plus one thing that
 * cannot be helped: SharePoint's Depot columns are Choice columns, and a choice
 * value has to exist before anything can be filed against it. See
 * provisioning/Add-LedgerDepot.ps1, and the note on the Locations screen.
 *
 * DEPOTS survives as the fallback for the moment before the query resolves —
 * without it the depot switcher flickers empty on every load, and a screen
 * defaulting to `depots[0]` would render nothing at all.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IDepot } from '../models';
import { qk } from '../query/keys';
import { useServices } from '../services/context';
import { DEPOTS } from '../services/depots';

/** Three letters for a badge. Typed where somebody bothered, derived where not —
    LEE for Leeds is right, and not worth making anybody type. */
function shortFor(name: string, typed: string): string {
  return typed.trim() || name.slice(0, 3).toUpperCase();
}

export function useDepots(): IDepot[] {
  const svc = useServices();
  const locations = useQuery({ queryKey: qk.locations, queryFn: () => svc.locations.all() });

  return useMemo(() => {
    const rows = Object.entries(locations.data ?? {});
    if (rows.length === 0) return DEPOTS;

    return rows
      .map(([name, site]) => ({
        id: name,
        name,
        sub: site.role,
        short: shortFor(name, site.short),
      }))
      /* Alphabetical, because the register has no order of its own and a list
         that reshuffles between loads is worse than one in the wrong order. */
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [locations.data]);
}
